import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '../../lib/api.js';
import { PageHeader, StatusPill } from '../../components/ui/Feedback.jsx';
import { Search, ChevronLeft, ChevronRight, Plus, Calendar, FileText } from 'lucide-react';

// =============================================================================
// QuotesListPage — Slice 7d.4 quote tool
// =============================================================================
//
// Mirror of BookingsListPage but for the `quotes` table. Lighter filter set
// because quotes don't carry chauffeur/vehicle/area/passenger details — those
// only exist once a quote converts to a booking.
//
// Layout:
//   - Same Bookings|Quotes toggle as the list page (rendered in BookingsListPage
//     and here so user can flip between them at any time)
//   - "New quote" CTA → /bookings/new?mode=quote (reuses the same form)
//   - Filter sidebar: status, date range (created_at), customer email, search
//   - Result rows: ref · status · recipient · total · created · expires
//   - Click row → /quotes/:id
//
// URL filters are persisted via setSearchParams (same pattern as BookingsListPage)
// so back-button + bookmarks just work.
// =============================================================================

function cn(...classes) {
    return classes.filter(Boolean).join(' ');
}

const STATUS_CONFIG = {
    draft:     { label: 'Draft',     pillStatus: 'neutral' },
    sent:      { label: 'Sent',      pillStatus: 'info'    },
    converted: { label: 'Converted', pillStatus: 'success' },
    expired:   { label: 'Expired',   pillStatus: 'warning' },
    cancelled: { label: 'Cancelled', pillStatus: 'neutral' },
};

const ALL_STATUSES = Object.keys(STATUS_CONFIG);

const DATE_PRESETS = [
    { value: 'today',        label: 'Today'        },
    { value: 'this_week',    label: 'This week'    },
    { value: 'last_week',    label: 'Last week'    },
    { value: 'last_month',   label: 'Last month'   },
    { value: 'last_quarter', label: 'Last quarter' },
];

const MOBILE_DATE_PRESETS = [
    { value: 'today',     label: 'Today'     },
    { value: 'this_week', label: 'This week' },
];

// Resolve preset → {from, to} YYYY-MM-DD strings. Same shape as BookingsListPage
// but applies to quote.created_at instead of pickup_date.
function presetToDates(preset) {
    const today = new Date();
    const fmt = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    };

    if (preset === 'today') {
        return { from: fmt(today), to: fmt(today) };
    }
    if (preset === 'this_week') {
        const day = today.getDay() || 7;
        const monday = new Date(today); monday.setDate(today.getDate() - (day - 1));
        const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
        return { from: fmt(monday), to: fmt(sunday) };
    }
    if (preset === 'last_week') {
        const day = today.getDay() || 7;
        const thisMonday = new Date(today); thisMonday.setDate(today.getDate() - (day - 1));
        const lastMonday = new Date(thisMonday); lastMonday.setDate(thisMonday.getDate() - 7);
        const lastSunday = new Date(lastMonday); lastSunday.setDate(lastMonday.getDate() + 6);
        return { from: fmt(lastMonday), to: fmt(lastSunday) };
    }
    if (preset === 'last_month') {
        const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const last  = new Date(today.getFullYear(), today.getMonth(), 0);
        return { from: fmt(first), to: fmt(last) };
    }
    if (preset === 'last_quarter') {
        const currentQuarter = Math.floor(today.getMonth() / 3);
        const lastQuarterStartMonth = (currentQuarter - 1) * 3;
        const isNewYear = currentQuarter === 0;
        const year = isNewYear ? today.getFullYear() - 1 : today.getFullYear();
        const startMonth = isNewYear ? 9 : lastQuarterStartMonth;
        const first = new Date(year, startMonth, 1);
        const last = new Date(year, startMonth + 3, 0);
        return { from: fmt(first), to: fmt(last) };
    }
    return { from: '', to: '' };
}

function shiftDateWindow(deltaDays, filters, updateFilters) {
    if (!filters.date_from || !filters.date_to) return;
    const fmt = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    };
    const [yFrom, mFrom, dFrom] = filters.date_from.split('-').map(Number);
    const [yTo, mTo, dTo] = filters.date_to.split('-').map(Number);
    const from = new Date(yFrom, mFrom - 1, dFrom);
    const to = new Date(yTo, mTo - 1, dTo);
    from.setDate(from.getDate() + deltaDays);
    to.setDate(to.getDate() + deltaDays);
    updateFilters({
        date_from: fmt(from),
        date_to: fmt(to),
        date_preset: '',
        offset: 0,
    });
}

// =============================================================================
// URL filter hook — same pattern as BookingsListPage's useUrlFilters
// =============================================================================
function useUrlFilters() {
    const [, setSearchParams] = useSearchParams();

    const initial = useMemo(() => {
        const params = new URLSearchParams(window.location.search);
        const get = (k) => params.get(k) || '';

        const statusFromUrl = params.has('status')
            ? get('status').split(',').filter(Boolean)
            : [];

        // Default landing: this_week (more useful than today for quotes —
        // the dispatcher's looking at "what's outstanding" not "what came
        // in today")
        const urlPreset = get('date_preset');
        const explicitFrom = get('date_from');
        const explicitTo = get('date_to');
        let dateFromInit, dateToInit, presetInit;
        if (urlPreset) {
            const resolved = presetToDates(urlPreset);
            dateFromInit = resolved.from;
            dateToInit = resolved.to;
            presetInit = urlPreset;
        } else if (explicitFrom || explicitTo) {
            dateFromInit = explicitFrom;
            dateToInit = explicitTo;
            presetInit = '';
        } else {
            const resolved = presetToDates('this_week');
            dateFromInit = resolved.from;
            dateToInit = resolved.to;
            presetInit = 'this_week';
        }

        return {
            date_preset:    presetInit,
            date_from:      dateFromInit,
            date_to:        dateToInit,
            status:         new Set(statusFromUrl),
            customer_email: get('customer_email'),
            search:         get('search'),
            sort:           get('sort') || 'created_desc',
            offset:         parseInt(get('offset') || '0', 10),
            limit:          parseInt(get('limit') || '50', 10),
        };
    }, []);

    const [filters, setFilters] = useState(initial);

    const update = (changes) => {
        const next = { ...filters, ...changes };
        setFilters(next);
        const params = new URLSearchParams();
        if (next.date_preset)   params.set('date_preset', next.date_preset);
        if (next.date_from)     params.set('date_from', next.date_from);
        if (next.date_to)       params.set('date_to', next.date_to);
        if (next.status.size > 0 && next.status.size < ALL_STATUSES.length) {
            params.set('status', [...next.status].join(','));
        }
        if (next.customer_email) params.set('customer_email', next.customer_email);
        if (next.search)         params.set('search', next.search);
        if (next.sort && next.sort !== 'created_desc') params.set('sort', next.sort);
        if (next.offset > 0)     params.set('offset', String(next.offset));
        if (next.limit !== 50)   params.set('limit', String(next.limit));
        setSearchParams(params, { replace: true });
    };

    const reset = () => {
        const week = presetToDates('this_week');
        update({
            date_preset: 'this_week', date_from: week.from, date_to: week.to,
            status: new Set(),
            customer_email: '',
            search: '',
            sort: 'created_desc',
            offset: 0,
        });
    };

    return [filters, update, reset];
}

function buildQueryString(filters) {
    const params = new URLSearchParams();
    if (filters.date_from)   params.set('date_from', filters.date_from);
    if (filters.date_to)     params.set('date_to', filters.date_to);
    if (filters.status.size > 0) params.set('status', [...filters.status].join(','));
    if (filters.customer_email) params.set('customer_email', filters.customer_email);
    if (filters.search)      params.set('search', filters.search);
    if (filters.sort)        params.set('sort', filters.sort);
    params.set('limit', String(filters.limit));
    params.set('offset', String(filters.offset));
    return params.toString();
}

function formatCreatedAt(input) {
    if (!input) return '—';
    const d = input instanceof Date ? input : new Date(input);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-GB', {
        weekday: 'short', day: '2-digit', month: 'short',
    }) + ' · ' + d.toLocaleTimeString('en-GB', {
        hour: '2-digit', minute: '2-digit',
    });
}

// Relative-time formatter for expires_at. Only meaningful for `sent` status.
// Returns "in 6 days", "in 4 hours", "expired" depending on the timestamp.
function formatExpiresIn(expiresAt, status) {
    if (status !== 'sent') return null;
    if (!expiresAt) return null;
    const exp = new Date(expiresAt);
    if (isNaN(exp.getTime())) return null;
    const now = new Date();
    const diffMs = exp.getTime() - now.getTime();
    if (diffMs <= 0) return { label: 'expired', className: 'text-red-700' };
    const diffHours = diffMs / 3600000;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays >= 2) {
        return { label: `expires in ${diffDays} days`, className: 'text-ink-subtle' };
    }
    if (diffHours >= 24) {
        return { label: 'expires in 1 day', className: 'text-amber-700' };
    }
    if (diffHours >= 1) {
        const h = Math.floor(diffHours);
        return { label: `expires in ${h} hour${h !== 1 ? 's' : ''}`, className: 'text-amber-700' };
    }
    const mins = Math.max(1, Math.floor(diffMs / 60000));
    return { label: `expires in ${mins} min`, className: 'text-red-700' };
}

// Build the master-total cell value. Single-ride quotes: pull from
// pricings[0].total. Multi-ride: master_total directly. Falls back to
// summing pricings if nothing's set.
function quoteTotalLabel(quote) {
    if (quote.master_total != null) {
        return `£${Number(quote.master_total).toFixed(2)}`;
    }
    const pricings = Array.isArray(quote.pricings) ? quote.pricings : [];
    if (pricings.length === 1) {
        const t = pricings[0]?.total;
        if (t != null) return `£${Number(t).toFixed(2)}`;
    }
    if (pricings.length > 1) {
        const sum = pricings.reduce((s, p) => s + (Number(p?.total) || 0), 0);
        if (sum > 0) return `£${sum.toFixed(2)}`;
    }
    return '—';
}

// Recipient name + email line. Falls back to email-only if name not stored,
// or "—" if neither (which is rare — quote create requires email when sending).
function quoteRecipientLabel(quote) {
    const linkedName = quote.linked_customer_name;
    const fallback = quote.customer_name;
    const email = quote.customer_email;
    const name = linkedName || fallback;
    if (name && email) return { name, email };
    if (email) return { name: null, email };
    if (name) return { name, email: null };
    return { name: null, email: null };
}

// =============================================================================
// Component
// =============================================================================

export default function QuotesListPage() {
    const api = useApi();
    const navigate = useNavigate();
    const [filters, updateFilters, resetFilters] = useUrlFilters();

    const queryString = buildQueryString(filters);
    const { data, isLoading, isError, error } = useQuery({
        queryKey: ['quotes', 'list', queryString],
        queryFn:  () => api.get(`/quotes?${queryString}`),
        keepPreviousData: true,
    });

    const isNarrowed = (
        filters.date_preset !== 'this_week' ||
        filters.status.size > 0 ||
        Boolean(filters.customer_email) ||
        Boolean(filters.search) ||
        (filters.sort && filters.sort !== 'created_desc')
    );

    const quotes = data?.quotes || [];
    const total  = data?.total ?? 0;
    const limit  = data?.limit ?? 50;
    const offset = data?.offset ?? 0;
    const pageStart = total === 0 ? 0 : offset + 1;
    const pageEnd   = Math.min(offset + quotes.length, total);

    const toggleStatus = (s) => {
        const next = new Set(filters.status);
        if (next.has(s)) next.delete(s);
        else next.add(s);
        updateFilters({ status: next, offset: 0 });
    };

    const goToQuote = (id) => navigate(`/quotes/${id}`);

    return (
        <div className="min-h-screen bg-canvas">
            <PageHeader
                title="Quotes"
                subtitle={`${total.toLocaleString()} total · showing ${pageStart}–${pageEnd}`}
            />

            {/* Mode toggle — same pattern as NewBookingPage. Bookings|Quotes
                lets dispatcher flip between the two list pages without going
                through the sidebar. Quotes is highlighted because we're on it. */}
            <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 pt-6 flex items-center justify-between gap-3 flex-wrap">
                <ListModeToggle current="quotes" />

                <Link
                    to="/bookings/new?mode=quote"
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 active:bg-emerald-800 transition-colors shadow-sm"
                >
                    <Plus className="w-4 h-4" />
                    New quote
                </Link>
            </div>

            <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 py-6 grid lg:grid-cols-[280px_1fr] gap-6">
                {/* ---- Filter sidebar (desktop only) ---- */}
                <aside className="hidden lg:block">
                    <div className="bg-white border border-line rounded-xl p-5 space-y-5">
                        <h2 className="text-sm font-semibold text-ink">Filters</h2>

                        {/* Date range — applies to created_at */}
                        <div className="space-y-2">
                            <label className="block text-xs font-medium text-muted uppercase tracking-wide">
                                Created
                            </label>
                            <div className="space-y-1">
                                {DATE_PRESETS.map(p => (
                                    <label key={p.value} className="flex items-center gap-2 cursor-pointer text-sm">
                                        <input
                                            type="radio"
                                            name="date_preset"
                                            checked={filters.date_preset === p.value}
                                            onChange={() => {
                                                const { from, to } = presetToDates(p.value);
                                                updateFilters({ date_preset: p.value, date_from: from, date_to: to, offset: 0 });
                                            }}
                                            className="text-ink focus:ring-ink"
                                        />
                                        <span className="text-ink">{p.label}</span>
                                    </label>
                                ))}
                                <div className="pt-2 space-y-1.5">
                                    <label className="block text-xs text-muted">Custom range</label>
                                    <div className="flex gap-2">
                                        <input
                                            type="date"
                                            value={filters.date_from}
                                            onChange={(e) => updateFilters({ date_from: e.target.value, date_preset: '', offset: 0 })}
                                            className="flex-1 min-w-0 text-xs px-2 py-1.5 border border-line rounded"
                                        />
                                        <input
                                            type="date"
                                            value={filters.date_to}
                                            onChange={(e) => updateFilters({ date_to: e.target.value, date_preset: '', offset: 0 })}
                                            className="flex-1 min-w-0 text-xs px-2 py-1.5 border border-line rounded"
                                        />
                                    </div>
                                    <div className="flex justify-center gap-1">
                                        <button
                                            type="button"
                                            onClick={() => shiftDateWindow(-1, filters, updateFilters)}
                                            disabled={!filters.date_from || !filters.date_to}
                                            className="px-3 py-1 rounded border border-line hover:bg-canvas disabled:opacity-30 disabled:cursor-not-allowed"
                                            aria-label="Shift dates earlier"
                                        >
                                            <ChevronLeft className="h-3.5 w-3.5" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => shiftDateWindow(1, filters, updateFilters)}
                                            disabled={!filters.date_from || !filters.date_to}
                                            className="px-3 py-1 rounded border border-line hover:bg-canvas disabled:opacity-30 disabled:cursor-not-allowed"
                                            aria-label="Shift dates later"
                                        >
                                            <ChevronRight className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Status */}
                        <div className="space-y-2">
                            <label className="block text-xs font-medium text-muted uppercase tracking-wide">Status</label>
                            <div className="space-y-1">
                                {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                                    <label key={key} className="flex items-center gap-2 cursor-pointer text-sm">
                                        <input
                                            type="checkbox"
                                            checked={filters.status.has(key)}
                                            onChange={() => toggleStatus(key)}
                                            className="text-ink focus:ring-ink"
                                        />
                                        <span className="text-ink">{cfg.label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Recipient email */}
                        <FilterInput
                            label="Recipient email"
                            placeholder="customer@example.com"
                            value={filters.customer_email}
                            onChange={(v) => updateFilters({ customer_email: v, offset: 0 })}
                        />

                        <button
                            type="button"
                            onClick={resetFilters}
                            className="w-full text-sm text-muted hover:text-ink underline"
                        >
                            Clear all filters
                        </button>
                    </div>
                </aside>

                {/* ---- Results ---- */}
                <div className="bg-white border border-line rounded-xl overflow-hidden">
                    <MobileFilterBar
                        filters={filters}
                        updateFilters={updateFilters}
                        isNarrowed={isNarrowed}
                        resetFilters={resetFilters}
                    />

                    {/* Top bar: search + sort */}
                    <div className="px-4 py-3 border-b border-line flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                        <div className="relative flex-1 max-w-md">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted pointer-events-none" />
                            <SearchInput
                                value={filters.search}
                                onChange={(v) => updateFilters({ search: v, offset: 0 })}
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <label className="text-xs text-muted">Sort:</label>
                            <select
                                value={filters.sort}
                                onChange={(e) => updateFilters({ sort: e.target.value, offset: 0 })}
                                className="text-sm px-2 py-1.5 border border-line rounded bg-white"
                            >
                                <option value="created_desc">Newest first</option>
                                <option value="created_asc">Oldest first</option>
                            </select>
                        </div>
                    </div>

                    {isLoading && !data ? (
                        <div className="px-6 py-16 text-center text-muted text-sm">Loading quotes…</div>
                    ) : isError && !data ? (
                        <div className="px-6 py-16 text-center">
                            <div className="text-sm text-red-600">{error?.message || 'Could not load quotes'}</div>
                        </div>
                    ) : quotes.length === 0 ? (
                        <div className="px-6 py-16 text-center">
                            <div className="text-muted text-sm">No quotes match these filters.</div>
                            <button
                                type="button"
                                onClick={resetFilters}
                                className="mt-3 text-sm text-ink underline"
                            >
                                Clear filters
                            </button>
                        </div>
                    ) : (
                        <ul className="divide-y divide-line">
                            {quotes.map(q => (
                                <QuoteRow key={q.id} quote={q} onClick={() => goToQuote(q.id)} />
                            ))}
                        </ul>
                    )}

                    {total > limit && (
                        <div className="px-4 py-3 border-t border-line flex items-center justify-between">
                            <div className="text-xs text-muted">
                                Showing {pageStart}–{pageEnd} of {total.toLocaleString()}
                            </div>
                            <div className="flex items-center gap-1">
                                <button
                                    type="button"
                                    disabled={offset === 0}
                                    onClick={() => updateFilters({ offset: Math.max(0, offset - limit) })}
                                    className="p-2 rounded hover:bg-canvas disabled:opacity-40 disabled:cursor-not-allowed"
                                    aria-label="Previous page"
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                </button>
                                <button
                                    type="button"
                                    disabled={pageEnd >= total}
                                    onClick={() => updateFilters({ offset: offset + limit })}
                                    className="p-2 rounded hover:bg-canvas disabled:opacity-40 disabled:cursor-not-allowed"
                                    aria-label="Next page"
                                >
                                    <ChevronRight className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// =============================================================================
// ListModeToggle — Bookings|Quotes button group, exported so BookingsListPage
// can render the same control without duplicating the markup.
// =============================================================================

export function ListModeToggle({ current }) {
    return (
        <div className="flex gap-1 p-1 bg-canvas rounded-lg w-fit">
            <Link
                to="/bookings"
                className={cn(
                    'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded transition-colors',
                    current === 'bookings'
                        ? 'bg-paper text-ink shadow-sm'
                        : 'text-ink-subtle hover:text-ink'
                )}
            >
                <Calendar className="w-4 h-4" />
                Bookings
            </Link>
            <Link
                to="/quotes"
                className={cn(
                    'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded transition-colors',
                    current === 'quotes'
                        ? 'bg-paper text-ink shadow-sm'
                        : 'text-ink-subtle hover:text-ink'
                )}
            >
                <FileText className="w-4 h-4" />
                Quotes
            </Link>
        </div>
    );
}

// =============================================================================
// Sub-components — mostly copies of BookingsListPage's helpers, simplified
// =============================================================================

function MobileFilterBar({ filters, updateFilters, isNarrowed, resetFilters }) {
    const [statusOpen, setStatusOpen] = useState(false);
    const statusCount = filters.status.size;
    const statusBadge = statusCount > 0 ? statusCount : null;

    const pickPreset = (presetValue) => {
        const { from, to } = presetToDates(presetValue);
        updateFilters({ date_preset: presetValue, date_from: from, date_to: to, offset: 0 });
    };

    const toggleStatus = (s) => {
        const next = new Set(filters.status);
        if (next.has(s)) next.delete(s);
        else next.add(s);
        updateFilters({ status: next, offset: 0 });
    };

    return (
        <div className="lg:hidden px-4 py-3 border-b border-line flex flex-wrap items-center gap-2">
            {MOBILE_DATE_PRESETS.map(p => {
                const active = filters.date_preset === p.value;
                return (
                    <button
                        key={p.value}
                        type="button"
                        onClick={() => pickPreset(p.value)}
                        className={cn(
                            'px-3 py-1.5 text-sm rounded-full border transition-colors',
                            active
                                ? 'bg-ink text-white border-ink'
                                : 'bg-white text-ink border-line hover:border-ink'
                        )}
                    >
                        {p.label}
                    </button>
                );
            })}

            <div className="relative">
                <button
                    type="button"
                    onClick={() => setStatusOpen(o => !o)}
                    className={cn(
                        'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-full border transition-colors',
                        statusBadge !== null
                            ? 'bg-ink text-white border-ink'
                            : 'bg-white text-ink border-line hover:border-ink'
                    )}
                    aria-expanded={statusOpen}
                    aria-haspopup="true"
                >
                    Status
                    {statusBadge !== null && (
                        <span className="text-xs opacity-80">({statusBadge})</span>
                    )}
                </button>

                {statusOpen && (
                    <>
                        <button
                            type="button"
                            onClick={() => setStatusOpen(false)}
                            aria-label="Close status filter"
                            className="fixed inset-0 z-30 bg-transparent cursor-default"
                        />
                        <div className="absolute z-40 left-0 mt-2 w-56 bg-white border border-line rounded-lg shadow-lg p-3 space-y-1.5">
                            {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                                <label key={key} className="flex items-center gap-2 cursor-pointer text-sm py-0.5">
                                    <input
                                        type="checkbox"
                                        checked={filters.status.has(key)}
                                        onChange={() => toggleStatus(key)}
                                        className="text-ink focus:ring-ink"
                                    />
                                    <span className="text-ink">{cfg.label}</span>
                                </label>
                            ))}
                        </div>
                    </>
                )}
            </div>

            {isNarrowed && (
                <button
                    type="button"
                    onClick={resetFilters}
                    className="inline-flex items-center gap-1 px-2 py-1.5 text-sm text-muted hover:text-ink"
                    aria-label="Clear all filters"
                >
                    Reset
                </button>
            )}
        </div>
    );
}

function SearchInput({ value, onChange }) {
    const [local, setLocal] = useState(value || '');
    useEffect(() => {
        if ((value || '') !== local) setLocal(value || '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);
    useEffect(() => {
        if ((local || '') === (value || '')) return;
        const t = setTimeout(() => onChange(local), 300);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [local]);

    return (
        <input
            type="search"
            placeholder="Search quote ref, email, name…"
            value={local}
            onChange={(e) => setLocal(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-line rounded-lg focus:outline-none focus:border-ink"
        />
    );
}

function FilterInput({ label, placeholder, value, onChange }) {
    const [local, setLocal] = useState(value || '');
    useEffect(() => {
        if ((value || '') !== local) setLocal(value || '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);
    useEffect(() => {
        if ((local || '') === (value || '')) return;
        const t = setTimeout(() => onChange(local), 300);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [local]);

    return (
        <div className="space-y-1">
            <label className="block text-xs font-medium text-muted uppercase tracking-wide">{label}</label>
            <input
                type="text"
                value={local}
                onChange={(e) => setLocal(e.target.value)}
                placeholder={placeholder}
                className="w-full text-sm px-2 py-1.5 border border-line rounded focus:outline-none focus:border-ink"
            />
        </div>
    );
}

// One row in the quotes list. Same hybrid layout as BookingRow:
//   - desktop: dense two-line row
//   - mobile/tablet: stacked card
function QuoteRow({ quote, onClick }) {
    const cfg = STATUS_CONFIG[quote.status] || { label: quote.status, pillStatus: 'neutral' };
    const recipient = quoteRecipientLabel(quote);
    const total = quoteTotalLabel(quote);
    const expiresIn = formatExpiresIn(quote.expires_at, quote.status);
    const rideCount = Array.isArray(quote.rides) ? quote.rides.length : 1;

    return (
        <li
            onClick={onClick}
            className="cursor-pointer hover:bg-canvas/60 transition-colors"
        >
            {/* Desktop */}
            <div className="hidden lg:grid grid-cols-[140px_1fr_120px_110px] gap-4 px-4 py-3 items-start">
                <div>
                    <div className="font-mono text-sm text-ink">{quote.quote_ref}</div>
                    <div className="text-xs text-muted">{formatCreatedAt(quote.created_at)}</div>
                </div>

                <div className="min-w-0 space-y-0.5">
                    {recipient.name && (
                        <div className="text-sm text-ink truncate">{recipient.name}</div>
                    )}
                    <div className="text-xs text-muted truncate">{recipient.email || '—'}</div>
                    <div className="text-xs text-muted">
                        {rideCount} ride{rideCount !== 1 ? 's' : ''}
                        {expiresIn && (
                            <>
                                <span className="text-muted"> · </span>
                                <span className={expiresIn.className}>{expiresIn.label}</span>
                            </>
                        )}
                    </div>
                </div>

                <div className="text-sm font-semibold text-ink text-right">
                    {total}
                </div>

                <div className="flex justify-end">
                    <StatusPill status={cfg.pillStatus}>{cfg.label}</StatusPill>
                </div>
            </div>

            {/* Mobile */}
            <div className="lg:hidden p-4 space-y-2">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <div className="font-mono text-sm text-ink">{quote.quote_ref}</div>
                        <div className="text-xs text-muted mt-0.5">{formatCreatedAt(quote.created_at)}</div>
                    </div>
                    <StatusPill status={cfg.pillStatus}>{cfg.label}</StatusPill>
                </div>
                <div className="text-sm text-ink">
                    {recipient.name || recipient.email || '—'}
                </div>
                {recipient.name && recipient.email && (
                    <div className="text-xs text-muted">{recipient.email}</div>
                )}
                <div className="flex items-center justify-between pt-1">
                    <div className="text-xs text-muted">
                        {rideCount} ride{rideCount !== 1 ? 's' : ''}
                        {expiresIn && (
                            <>
                                <span> · </span>
                                <span className={expiresIn.className}>{expiresIn.label}</span>
                            </>
                        )}
                    </div>
                    <div className="text-sm font-semibold text-ink">{total}</div>
                </div>
            </div>
        </li>
    );
}
